import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { useEventTracking } from "react-event-tracker";
import { Button, Container, Message, Modal, Text } from "basis";
import CurrencyInput from "react-currency-masked-input";
import { formatDueDate } from "library/utils";
import { FormActions } from "components/FormActions/FormActions";

import { CurrencyFormat } from "../../CurrencyFormat";

import styles from "./payment-amounts.module.scss";

function ModalContent({
  amountOverDue,
  minimumPayment,
  totalAmount,
  setOpen,
  combineAmount,
}) {
  return (
    <Container>
      <Text color="grey.t75">
        <b>
          Payments may take up to 3 days to clear and you won't be able to make
          another bank transfer for 5 days. We recommend combining your payment
          so you don't miss your due date.
        </b>
      </Text>
      <Text color="grey.t75" margin="4 0 0 0">
        Your overdue or overlimit is{" "}
        <b>
          <CurrencyFormat value={amountOverDue} />
        </b>{" "}
        and your minimum payment is{" "}
        <b>
          <CurrencyFormat value={minimumPayment} />
        </b>
        .
      </Text>
      <Text color="grey.t75" margin="4 0 0 0">
        Would you like to combine them into one payment of{" "}
        <b>
          <CurrencyFormat value={totalAmount} />
        </b>
        ?
      </Text>
      <Modal.Footer>
        <Container width="100%">
          <FormActions switchLayoutAt="xs">
            <FormActions.Primary>
              <Button
                width="100%"
                variant="primary"
                onClick={() => combineAmount("customAmount")}
              >
                Yes, combine
              </Button>
            </FormActions.Primary>
            <FormActions.Secondary>
              <Button
                width="100%"
                variant="secondary"
                onClick={() => setOpen(false)}
              >
                No, don't combine
              </Button>
            </FormActions.Secondary>
          </FormActions>
        </Container>
      </Modal.Footer>
    </Container>
  );
}

const amountLowerThanMinimumMessage =
  "The amount entered is lower than your minimum amount. You can still proceed with this payment, but you may incur additional fees and receive phone calls from us if you are not able to meet your monthly payment obligations.";

function AmountField({
  amountKey,
  title,
  description,
  amount,
  date,
  textColor,
  isRadio,
  radioEnabled,
  isChecked,
  radioOnChange,
  disabled,
  customAmountDetails,
  minimumMonthlyRepayment,
  creditLimit,
  currentBalance,
  htmlElRef,
}) {
  const labelContent = (
    <>
      <div>
        <span className={`${styles.title} ${textColor}`}>{title}</span>
      </div>
      <div className={`body02 ${styles.description}`}>{description}</div>
    </>
  );

  const validateAmount = (amt) => {
    const maxAmount = Math.min(currentBalance + 200, creditLimit * 1.1);
    return parseFloat(amt) > maxAmount
      ? "Payment amount cannot exceed current balance"
      : null;
  };

  const getAmountMessage = (amt) =>
    amountKey === "customAmount" && amt < minimumMonthlyRepayment
      ? amountLowerThanMinimumMessage
      : "";

  return (
    <div
      key={amountKey}
      className={`${styles.amountField} ${
        amountKey === "customAmount" ? styles.amountFieldCustom : ""
      } ${isChecked ? styles.selectedAmount : ""}`}
    >
      {isRadio ? (
        <div className={styles.radioWrapper}>
          <input
            type="radio"
            id={`${amountKey}-radio`}
            checked={isChecked}
            onChange={radioOnChange}
          />
          <label htmlFor={`${amountKey}-radio`} className={styles.text}>
            {labelContent}
          </label>
        </div>
      ) : (
        <div className={`${styles.text} ${radioEnabled ? styles.noRadio : ""}`}>
          {labelContent}
        </div>
      )}

      <div className={styles.amountWrapper}>
        {amountKey === "customAmount" ? (
          <CurrencyInput
            id="customAmount"
            type="number"
            ref={htmlElRef}
            className={`${
              customAmountDetails.amountError ? styles.inputError : ""
            }`}
            placeholder="$0.00"
            aria-describedby="customAmountDetailsMessage"
            disabled={disabled}
            defaultValue={parseFloat(customAmountDetails.amount).toFixed(2)}
            pattern="/^\d+(\.\d+)?$/"
            onBlur={(e) => {
              customAmountDetails.updateCustomAmountState({
                amount: parseFloat(e.target.value),
                amountError: validateAmount(e.target.value),
                message: getAmountMessage(e.target.value),
              });
            }}
            onKeyDown={(e) => {
              // disallow 'e' character
              if (e.keyCode === 69) {
                e.preventDefault();
              }
            }}
            error={customAmountDetails.amountError || ""}
          />
        ) : (
          <>
            <div className={`${styles.amount} ${textColor}`}>
              <CurrencyFormat value={amount} />
            </div>
            {date && (
              <div className={styles.date}>
                as at {format(new Date(date), "dd MMM")}
              </div>
            )}
          </>
        )}
      </div>
      {amountKey === "customAmount" ? (
        <div className={`body02 ${styles.error}`}>
          {customAmountDetails.amountError}
        </div>
      ) : null}
      {amountKey === "customAmount" && customAmountDetails.message && (
        <Message role="status" severity="info-or-minor">
          <span id="customAmountDetailsMessage">
            {customAmountDetails.message}
          </span>
        </Message>
      )}
    </div>
  );
}

// TODO: change interface from snake_case to camelCase
export const PaymentAmounts = ({
  minimum_monthly_repayment: minimumMonthlyRepayment,
  current_balance: currentBalance,
  amount_immediate_due: amountImmediateDue,
  payment_due: paymentDue,
  last_statement: lastStatement,
  creditLimit,
  prfi = {},
  onSelectPaymentAmount,
  selectedOption,
  customAmountDetails,
  setOverlayOpen,
}) => {
  const { trackEvent } = useEventTracking();
  const [open, setOpen] = useState(false);
  const [amounts, setAmounts] = useState([]);
  const [checkedRadio, setCheckedRadio] = useState(selectedOption);
  const [amountDisabled, setAmountDisabled] = useState(
    selectedOption !== "customAmount",
  );

  const htmlElRef = useRef(null);

  const setModalOpen = (val) => {
    setOpen(val);
    setOverlayOpen(val);
  };

  const radioOnChange = (i, id) => {
    setCheckedRadio(id);
    setAmountDisabled(id !== "customAmount");
    if (htmlElRef && htmlElRef.current) htmlElRef.current.state.value = "";
    customAmountDetails.updateCustomAmountState({
      amount: id !== "customAmount" ? "" : customAmountDetails.amount,
      amountError: null,
    });
    if (id === "amountImmediateDue" || id === "minimumMonthlyRepayment") {
      setModalOpen(amountImmediateDue > 0 && minimumMonthlyRepayment > 0);
    }
    const { amount } = amounts[i];

    trackEvent({
      event: {
        category: "radio-button",
        action: "calculator",
        location: "Choose amount",
        label: id,
      },
    });
    onSelectPaymentAmount({
      amount: amount || 0,
      selectedOption: id,
    });
  };

  const combineAmount = (id) => {
    setCheckedRadio(id);
    setAmountDisabled(id !== "customAmount");
    const combinedAmount = amountImmediateDue + minimumMonthlyRepayment;
    customAmountDetails.updateCustomAmountState({
      amount: combinedAmount,
      amountError: null,
    });
    if (htmlElRef && htmlElRef.current)
      htmlElRef.current.state.value = parseFloat(combinedAmount).toFixed(2);
    onSelectPaymentAmount({
      amount: combinedAmount || 0,
      selectedOption: id,
    });
    setModalOpen(false);
  };

  useEffect(() => {
    const allAmounts = [];
    if (amountImmediateDue > 0) {
      allAmounts.push({
        amountKey: "amountImmediateDue",
        title: "Overdue or overlimit (due immediately)",
        description: "Pay to get up to date",
        amount: amountImmediateDue,
        textColor: styles.overdue,
      });
    }

    allAmounts.push({
      amountKey: "minimumMonthlyRepayment",
      title:
        minimumMonthlyRepayment > 0
          ? `Minimum payment (due by ${formatDueDate(paymentDue)})`
          : "Minimum payment",
      description:
        minimumMonthlyRepayment > 0
          ? "Pay the minimum for this period"
          : "You're up to date!",
      amount: minimumMonthlyRepayment,
    });

    if (prfi.value > 0 && prfi.enabled) {
      allAmounts.push({
        amountKey: "prfi",
        title: "Reduce future interest",
        description: "Pay to save on interest",
        amount: prfi.value,
        date: lastStatement.date,
      });
    }

    if (lastStatement && currentBalance < creditLimit * 1.1) {
      allAmounts.push({
        amountKey: "closingBalance",
        title: "Statement balance",
        description: "Pay the last statement balance",
        amount: lastStatement.closing_balance,
        date: lastStatement.date,
      });
    }

    if (currentBalance < creditLimit * 1.1) {
      allAmounts.push({
        amountKey: "currentBalance",
        title: "Current balance",
        description: "Pay to clear balance, excludes pending transactions",
        amount: currentBalance,
      });
    }

    allAmounts.push({
      amountKey: "customAmount",
      title: "Enter an amount",
      amount: customAmountDetails.amount,
    });

    setAmounts(allAmounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prfi.value, prfi.enabled]);

  return (
    <div className={styles.amounts}>
      <div className={styles.amountDisplay}>
        {amounts.map((amount, i) => (
          <div key={i}>
            <AmountField
              {...amount}
              radioEnabled
              isRadio={amount.amount > 0 || amount.amountKey === "customAmount"}
              isChecked={checkedRadio === amount.amountKey}
              radioOnChange={() => radioOnChange(i, amount.amountKey)}
              disabled={amountDisabled}
              customAmountDetails={customAmountDetails}
              creditLimit={creditLimit}
              currentBalance={currentBalance}
              htmlElRef={htmlElRef}
              minimumMonthlyRepayment={minimumMonthlyRepayment}
            />
          </div>
        ))}
      </div>
      <Modal
        title="We recommend paying the total amount due"
        open={open}
        onClose={() => setModalOpen(false)}
      >
        <ModalContent
          amountOverDue={amountImmediateDue}
          minimumPayment={minimumMonthlyRepayment}
          totalAmount={amountImmediateDue + minimumMonthlyRepayment}
          combineAmount={combineAmount}
          setOpen={setModalOpen}
        />
      </Modal>
    </div>
  );
};
